import React from "react";
import { Link, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import { IoIosRefresh } from "react-icons/io";
import { IoCopyOutline } from "react-icons/io5";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "./Header";
import OrderArtworkButton from "./OrderArtworkButton";
import * as spOrderStatsService from "../services/subprintStats.service";

// Shows 0 (not blank) when a value is missing, with thousands separators (e.g. 3,482).
const formatNumber = (value) => (value ?? 0).toLocaleString();

// order_items.artwork_path is already stored once an order is composited — the backend
// sends the ready-made URL (see fetch_sp_orders_report), so OrderArtworkButton can show
// it directly instead of making a live call to the external barcode-lookup API.
const toPreloadedArtworks = (items) =>
  (items || [])
    .filter((item) => item.artwork_path)
    .map((item) => ({
      barcode_id: item.order_item_id,
      artwork_path: item.artwork_path,
    }));

// Small inline spinner shown in place of a summary card's number while it's still
// loading — otherwise every card briefly (and misleadingly) reads "0".
const CardSpinner = ({ className = "h-7 w-7" }) => (
  <span
    role="status"
    aria-label="Loading"
    className={`inline-block ${className} border-[3px] border-white/20 border-t-white rounded-full animate-spin`}
  />
);

// "YYYY-MM-DD" -> "October 26 - Monday". Parsed with an explicit local time
// so it doesn't shift a day when the browser isn't in UTC.
const formatCalendarDate = (dateStr) => {
  const date = new Date(`${dateStr}T00:00:00`);
  if (isNaN(date.getTime())) return dateStr;
  const monthDay = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  return `${monthDay} - ${weekday}`;
};

// Current date/time in Pacific time (business timezone), regardless of the
// viewer's own browser timezone. Uses Intl instead of a fixed UTC offset so
// the PST/PDT switch is handled automatically.
const getPacificNow = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type).value;
  return {
    dateStr: `${get("year")}-${get("month")}-${get("day")}`,
    hour: parseInt(get("hour"), 10),
    minute: parseInt(get("minute"), 10),
  };
};

// Due dates come from the API as "YYYY-MM-DD". Dates are compared as plain
// strings — a millisecond diff would misfire around DST transitions.
// Returns:
//   'very-late' — due date is any calendar day before today
//   'late'      — due today, past the 2:30pm Pacific cutoff
//   null        — not late yet
const getLateStatus = (dueDateStr) => {
  if (!dueDateStr) return null;
  const due = dueDateStr.slice(0, 10);
  const { dateStr: todayStr, hour, minute } = getPacificNow();
  if (due < todayStr) return "very-late";
  if (due === todayStr && (hour > 14 || (hour === 14 && minute >= 30))) {
    return "late";
  }
  return null;
};

const TAB_CONFIG = {
  total: {
    title: "Total Late Orders",
    subtitle: "Open orders whose due date has already passed.",
    emptyMessage: "No late orders found.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getTotalLateOrdersReport,
    labelColor: "text-red-300",
  },
  allorders: {
    title: "Total Orders",
    subtitle: "All orders currently in the SubPrint production queue.",
    emptyMessage: "No orders yet.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getAllOrdersReport,
    labelColor: "text-purple-300",
  },
  errors: {
    title: "Shipping Errors",
    subtitle: "Open orders with a shipping exception on file.",
    emptyMessage: "No shipping errors found.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getShipErrorsReport,
    labelColor: "text-amber-300",
  },
  shipped: {
    title: "Orders Shipped Today",
    subtitle: "Orders successfully shipped today.",
    emptyMessage: "No orders shipped today yet.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getShippedTodayReport,
    dateLabel: "Shipped Date",
    dateField: "shipped_date",
    labelColor: "text-green-300",
  },
  nextday: {
    title: "Orders Due Next Business Day",
    subtitle: "Orders that will be due next business day.",
    emptyMessage: "No orders due next business day.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getDueNextBusinessDayReport,
    labelColor: "text-teal-300",
  },
  mugboxes: {
    title: "Mug Box Requirements",
    subtitle: "Open orders behind the upcoming mug box requirements.",
    emptyMessage: "No eligible mug orders found.",
    emptySearchMessage: "No orders match your search.",
    fetch: spOrderStatsService.getMugBoxOrdersReport,
    labelColor: "text-teal-300",
  },
};

const VALID_TABS = ["total", "allorders", "errors", "shipped", "nextday", "mugboxes"];
const resolveTab = (tab) => (VALID_TABS.includes(tab) ? tab : "total");

// Big glanceable summary cards — color-coded by meaning so workers can read the
// counts at a distance. Each card links to its matching tab.
const SUMMARY_CARDS = [
  {
    key: "allorders",
    label: "Total Orders",
    to: "/subprint/orders/allorders",
    field: "totalOrders",
    itemField: "totalItems",
    orderLabel: "Total Orders",
    itemLabel: "Total Items",
    accent: "border-purple-500",
    labelColor: "text-purple-300",
  },
  {
    key: "total",
    label: "Total Late Orders",
    to: "/subprint/orders",
    field: "totalLateOrders",
    itemField: "totalLateItems",
    orderLabel: "Orders Late",
    itemLabel: "Items Late",
    accent: "border-red-500",
    labelColor: "text-red-300",
  },
  {
    key: "errors",
    label: "Shipping Errors",
    to: "/subprint/orders/errors",
    field: "shippingErrors",
    itemField: "shippingErrorItems",
    orderLabel: "Orders",
    itemLabel: "Items",
    accent: "border-amber-500",
    labelColor: "text-amber-300",
  },
  {
    key: "shipped",
    label: "Orders Shipped Today",
    to: "/subprint/orders/shipped",
    field: "shippedToday",
    itemField: "shippedTodayItems",
    orderLabel: "Orders Shipped",
    itemLabel: "Items Shipped",
    accent: "border-green-500",
    labelColor: "text-green-300",
  },
  {
    key: "nextday",
    label: "Orders Due Next Business Day",
    to: "/subprint/orders/next-business-day",
    field: "dueNextBusinessDay",
    itemField: "dueNextBusinessDayItems",
    orderLabel: "Orders",
    itemLabel: "Items",
    accent: "border-teal-500",
    labelColor: "text-teal-300",
  },
];

class SpOpenOrdersPage extends React.Component {
  constructor(props) {
    super(props);
    this.searchTimer = null;
    const initialTab = resolveTab(props.tab);
    this.state = {
      notLoggedIn: false,
      activeTab: initialTab,
      loading: true,
      error: null,
      search: "",
      page: 1,
      perPage: 50,
      count: 0,
      itemCount: 0,
      totalPages: 1,
      orders: [],
      expanded: {},
      mugBoxRequirements: [],
      summaryLoading: true,
      summary: {
        totalOrders: null,
        totalItems: null,
        totalLateOrders: null,
        totalLateItems: null,
        shippingErrors: null,
        shippingErrorItems: null,
        shippedToday: null,
        shippedTodayItems: null,
        dueNextBusinessDay: null,
        dueNextBusinessDayItems: null,
      },
    };
  }

  componentDidMount() {
    const currEmail = localStorage.getItem("UserEmail");
    const cookie = Cookies.get("token");
    if (!currEmail || !cookie) {
      this.setState({ notLoggedIn: true, loading: false });
      return;
    }
    this.fetchSummary();
    this.fetchOrders();
    this.fetchBoxRequirements();
  }

  // Backs the Upcoming Mug Box Requirements card — SubPrint has no puzzle
  // equivalent, so unlike OP's combined endpoint this only ever returns mug dates.
  fetchBoxRequirements = () => {
    spOrderStatsService
      .getBoxRequirements()
      .then((response) => {
        this.setState({ mugBoxRequirements: response.mug?.dates || [] });
      })
      .catch((err) => {
        console.error("[SpOpenOrdersPage] fetchBoxRequirements failed", err);
      });
  };

  componentDidUpdate(prevProps) {
    const nextTab = resolveTab(this.props.tab);
    const prevTab = resolveTab(prevProps.tab);
    if (nextTab !== prevTab) {
      this.setState(
        { activeTab: nextTab, page: 1, search: "", expanded: {} },
        () => {
          this.fetchOrders();
        },
      );
    }
  }

  componentWillUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }

  fetchSummary = () => {
    this.setState({ summaryLoading: true });
    spOrderStatsService
      .getOpenOrdersSummary()
      .then((response) => {
        this.setState({
          summaryLoading: false,
          summary: {
            totalOrders: response.totalOrders,
            totalItems: response.totalItems,
            totalLateOrders: response.totalLateOrders,
            totalLateItems: response.totalLateItems,
            shippingErrors: response.shippingErrors,
            shippingErrorItems: response.shippingErrorItems,
            shippedToday: response.shippedToday,
            shippedTodayItems: response.shippedTodayItems,
            dueNextBusinessDay: response.dueNextBusinessDay,
            dueNextBusinessDayItems: response.dueNextBusinessDayItems,
          },
        });
      })
      .catch((err) => {
        console.error("[SpOpenOrdersPage] fetchSummary failed", err);
        this.setState({ summaryLoading: false });
      });
  };

  fetchOrders = () => {
    this.fetchOrdersWithSearch(this.state.search, this.state.page);
  };

  handleSearchChange = (event) => {
    const search = event.target.value;
    this.setState({ search, page: 1 });
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      this.fetchOrdersWithSearch(search, 1);
    }, 400);
  };

  fetchOrdersWithSearch = (search, page) => {
    const { activeTab } = this.state;
    const tabConfig = TAB_CONFIG[activeTab];
    const fetchOptions = { search, page, per_page: this.state.perPage };
    // Optional deep-link support: a ?date=...&box_size=... in the URL narrows the
    // mug box tab to just the orders behind that bucket. The card itself links to
    // the plain unfiltered tab, but this still works if ever linked to directly.
    if (activeTab === "mugboxes") {
      const params = new URLSearchParams(window.location.search);
      const date = params.get("date");
      const boxSize = params.get("box_size");
      if (date) fetchOptions.date = date;
      if (boxSize) fetchOptions.box_size = boxSize;
    }
    this.setState({ loading: true, error: null });
    tabConfig
      .fetch(fetchOptions)
      .then((response) => {
        this.setState({
          loading: false,
          count: response.count,
          itemCount: response.item_count ?? 0,
          totalPages: response.total_pages,
          page: response.page,
          orders: response.orders || [],
          expanded: {},
        });
      })
      .catch((err) => {
        console.error("[SpOpenOrdersPage] fetch failed", {
          activeTab: this.state.activeTab,
          err,
        });
        this.setState({
          loading: false,
          error: "Unable to load orders. Please try again.",
          orders: [],
          itemCount: 0,
        });
      });
  };

  goToPage = (nextPage) => {
    if (
      nextPage < 1 ||
      nextPage > this.state.totalPages ||
      nextPage === this.state.page
    ) {
      return;
    }
    this.setState({ page: nextPage }, () => {
      this.fetchOrdersWithSearch(this.state.search, nextPage);
    });
  };

  toggleExpanded = (orderId) => {
    this.setState((prev) => ({
      expanded: {
        ...prev.expanded,
        [orderId]: !prev.expanded[orderId],
      },
    }));
  };

  renderPagination = () => {
    const { page, totalPages, count, perPage } = this.state;
    if (count <= perPage) {
      return null;
    }
    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, count);
    return (
      <div className="flex flex-wrap items-center justify-between mt-4 text-sm text-gray-300">
        <span>
          Showing {start}–{end} of {count} orders
        </span>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => this.goToPage(page - 1)}
            disabled={page <= 1}
            className="px-3 py-1 rounded bg-gray-700 disabled:opacity-40 hover:bg-gray-600"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => this.goToPage(page + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 rounded bg-gray-700 disabled:opacity-40 hover:bg-gray-600"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  renderItems = (order) => {
    if (!order.items || !order.items.length) {
      return (
        <tr>
          <td
            colSpan="10"
            className="px-4 py-3 text-sm text-gray-400 italic bg-gray-900"
          >
            No items found for this order.
          </td>
        </tr>
      );
    }
    return order.items.map((item) => (
      <tr
        key={item.order_item_id}
        className="bg-gray-900 border-b border-gray-700"
      >
        <td colSpan="2" className="px-4 py-2 text-sm text-gray-400">
          {item.order_item_id}
        </td>
        <td colSpan="2" className="px-4 py-2 text-sm text-white font-mono">
          {item.sku}
        </td>
        <td colSpan="3" className="px-4 py-2 text-sm text-white">
          {item.description}
        </td>
        <td colSpan="1" className="px-4 py-2 text-sm text-white text-right">
          {item.qty}
        </td>
        <td
          colSpan="2"
          className="px-4 py-2 text-sm text-white"
          onClick={(e) => e.stopPropagation()}
        >
          <OrderArtworkButton
            orderId={order.order_id}
            preloadedArtworks={toPreloadedArtworks([item])}
            system="SP"
            size="sm"
          />
        </td>
      </tr>
    ));
  };

  render() {
    const {
      notLoggedIn,
      activeTab,
      loading,
      error,
      search,
      count,
      itemCount,
      orders,
      expanded,
      summary,
      summaryLoading,
      mugBoxRequirements,
    } = this.state;
    const tabConfig = TAB_CONFIG[activeTab];
    const emptyMessage = search
      ? tabConfig.emptySearchMessage
      : tabConfig.emptyMessage;

    if (notLoggedIn) {
      return <Navigate to="/" replace={true} />;
    }

    const copyItem = (item) => {
      navigator.clipboard.writeText(item);
      toast(`Copied ${item} to Clipboard!`);
      return;
    };

    return (
      <div className="grid grid-cols-10 gap-2">
        <div className="col-span-10">
          <Header compNum={2} system="SP" />
          <div className="mt-2 px-2 pb-8">
            <div className="bg-gray-800 rounded-lg shadow-lg border-4 border-yellow-500 p-3 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                  <h1
                    className={`text-3xl sm:text-4xl lg:text-5xl font-semibold ${tabConfig.labelColor}`}
                  >
                    {tabConfig.title}
                  </h1>
                  <p className="text-base sm:text-lg lg:text-xl text-white mt-1">
                    {tabConfig.subtitle}
                  </p>
                  {activeTab === "mugboxes" &&
                    (() => {
                      const params = new URLSearchParams(window.location.search);
                      const date = params.get("date");
                      const boxSize = params.get("box_size");
                      if (!date && !boxSize) return null;
                      return (
                        <p className="text-sm text-teal-300 mt-1">
                          Filtered to{" "}
                          {date ? formatCalendarDate(date) : "all upcoming dates"}
                          {boxSize ? ` — ${boxSize} boxes` : ""}
                        </p>
                      );
                    })()}
                  <p className="text-sm font-medium text-gray-200 mt-2">
                    {formatNumber(count)} Orders
                    <span className="mx-2 text-gray-500">•</span>
                    {formatNumber(itemCount)} Items
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-300">Refresh</label>
                  <button
                    type="button"
                    onClick={() => {
                      this.fetchSummary();
                      this.fetchOrders();
                      this.fetchBoxRequirements();
                    }}
                    className="text-white hover:text-green-400"
                    title="Refresh"
                  >
                    <IoIosRefresh size={22} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-8">
                {SUMMARY_CARDS.map((card) => {
                  const isActive = activeTab === card.key;
                  const orderValue = summary[card.field];
                  const itemValue = summary[card.itemField];
                  return (
                    <Link
                      key={card.key}
                      to={card.to}
                      className={`flex flex-col rounded-2xl border-2 bg-gray-700/40 px-3 py-4 sm:px-6 sm:py-6 transition-all hover:bg-gray-700/70 hover:scale-[1.02] ${
                        card.accent
                      } ${
                        isActive
                          ? "ring-4 ring-white/20 shadow-xl border-4"
                          : "shadow-md"
                      }`}
                    >
                      <p
                        className={`text-xs sm:text-lg font-bold uppercase tracking-wider ${card.labelColor}`}
                      >
                        {card.label}
                      </p>
                      <div className="mt-3">
                        <p className="flex h-9 sm:h-14 lg:h-[3.75rem] items-center text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-none text-white tabular-nums">
                          {summaryLoading ? (
                            <CardSpinner className="h-7 w-7 sm:h-10 sm:w-10" />
                          ) : (
                            formatNumber(orderValue)
                          )}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-wide text-gray-400">
                          {card.orderLabel}
                        </p>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="flex h-6 sm:h-9 lg:h-10 items-center text-xl sm:text-3xl lg:text-4xl font-bold leading-none text-white tabular-nums">
                          {summaryLoading ? (
                            <CardSpinner className="h-5 w-5 sm:h-7 sm:w-7" />
                          ) : (
                            formatNumber(itemValue)
                          )}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-wide text-gray-400">
                          {card.itemLabel}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {mugBoxRequirements.length > 0 && (
                <Link
                  to="/subprint/orders/mug-boxes"
                  className="block rounded-2xl border-2 border-teal-500 bg-gray-700/40 hover:bg-gray-700/70 transition-colors px-4 py-4 sm:px-6 sm:py-6 shadow-md mb-8"
                >
                  <p className="text-base sm:text-lg font-bold uppercase tracking-wider text-teal-300 mb-4">
                    Upcoming Mug Box Requirements
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    {mugBoxRequirements.map((day) => {
                      const totalBoxes = day.boxes.reduce(
                        (sum, box) => sum + box.count,
                        0,
                      );
                      return (
                        <div
                          key={day.date}
                          className="sm:border-l sm:border-white/10 sm:first:border-l-0 sm:pl-6 sm:first:pl-0"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs uppercase tracking-wide text-gray-400">
                              {formatCalendarDate(day.date)}
                            </p>
                            {totalBoxes > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-600 text-white text-xs font-bold tabular-nums whitespace-nowrap">
                                {totalBoxes} box{totalBoxes === 1 ? "" : "es"}
                              </span>
                            )}
                          </div>
                          {day.boxes.length === 0 ? (
                            <p className="text-sm text-gray-500 italic mt-2">
                              No boxes needed
                            </p>
                          ) : (
                            <ul className="mt-2 space-y-1">
                              {day.boxes.map((box) => (
                                <li
                                  key={box.size}
                                  className="text-sm text-white flex justify-between gap-2"
                                >
                                  <span className="font-bold tabular-nums">
                                    {box.count}
                                  </span>
                                  <span className="text-gray-300">
                                    {box.size}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Link>
              )}

              <div className="flex overflow-x-auto border-b border-gray-600 mb-6">
                {SUMMARY_CARDS.map((card) => (
                  <Link
                    key={card.key}
                    to={card.to}
                    className={`flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      activeTab === card.key
                        ? "border-blue-500 text-white"
                        : "border-transparent text-gray-400 hover:text-white"
                    }`}
                  >
                    <span className="block">{card.label}</span>
                    <span className="block text-xs font-normal text-gray-400">
                      {summaryLoading ? (
                        <CardSpinner className="h-3 w-3" />
                      ) : (
                        <>
                          {formatNumber(summary[card.field])} orders ·{" "}
                          {formatNumber(summary[card.itemField])} items
                        </>
                      )}
                    </span>
                  </Link>
                ))}
                <Link
                  to="/subprint/orders/mug-boxes"
                  className={`flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === "mugboxes"
                      ? "border-blue-500 text-white"
                      : "border-transparent text-gray-400 hover:text-white"
                  }`}
                >
                  <span className="block">Mug Box Requirements</span>
                  <span className="block text-xs font-normal text-gray-400">
                    {formatNumber(
                      mugBoxRequirements.reduce(
                        (sum, day) =>
                          sum + day.boxes.reduce((s, b) => s + b.count, 0),
                        0,
                      ),
                    )}{" "}
                    boxes
                  </span>
                </Link>
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  value={search}
                  onChange={this.handleSearchChange}
                  placeholder="Search by Order ID or Customer Order ID"
                  className="w-full sm:flex-1 sm:min-w-[260px] max-w-xl px-4 py-2 text-sm text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="mb-4 px-4 py-3 text-sm text-red-200 bg-red-900 rounded border border-red-700">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-16">
                  <svg
                    role="status"
                    className="inline w-10 h-10 text-gray-200 animate-spin fill-gray-400"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </div>
              ) : !error && orders.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto shadow-md sm:rounded-lg">
                    <table className="w-full text-sm text-left text-gray-300">
                      <thead className="text-xs uppercase bg-gray-700 text-gray-300">
                        <tr>
                          <th className="px-3 py-3 w-8" />
                          <th className="px-3 py-3">Order ID</th>
                          <th className="px-3 py-3">Customer Order ID</th>
                          <th className="px-3 py-3">Source</th>
                          <th className="px-3 py-3">
                            {tabConfig.dateLabel || "Due Date"}
                          </th>
                          <th className="px-3 py-3">Notes</th>
                          {activeTab === "mugboxes" && (
                            <>
                              <th className="px-3 py-3">Qty</th>
                              <th className="px-3 py-3">Box Size</th>
                            </>
                          )}
                          <th className="px-3 py-3">Status</th>
                          <th className="px-3 py-3">Item Count</th>
                          <th className="px-3 py-3">SKU List</th>
                          <th className="px-3 py-3">Artwork</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => {
                          const isExpanded = !!expanded[order.order_id];
                          const dueDateValue = tabConfig.dateField
                            ? order[tabConfig.dateField]
                            : order.due_date || order.due_at;
                          const hasNote = !!(
                            order.notes && String(order.notes).trim()
                          );
                          // "Total Late Orders" includes due-today-or-earlier orders.
                          // Overdue (due before today) is always "very late"; due today
                          // only becomes "late" after the 2:30pm Pacific cutoff — same
                          // rule as OpenPrint. A note means someone's already handling
                          // it, so the highlight clears either way.
                          const lateStatus =
                            activeTab === "total"
                              ? getLateStatus(dueDateValue)
                              : null;
                          const veryLate = lateStatus === "very-late" && !hasNote;
                          const lateToday = lateStatus === "late" && !hasNote;
                          return (
                            <React.Fragment key={order.order_id}>
                              <tr
                                className={`border-b border-gray-700 cursor-pointer ${
                                  veryLate
                                    ? "bg-red-900 hover:bg-red-800"
                                    : lateToday
                                      ? "bg-red-700/40 hover:bg-red-600/50"
                                      : "bg-gray-800 hover:bg-gray-750"
                                }`}
                                onClick={() =>
                                  this.toggleExpanded(order.order_id)
                                }
                              >
                                <td className="px-3 py-3 text-gray-400">
                                  {isExpanded ? (
                                    <IoChevronDown />
                                  ) : (
                                    <IoChevronForward />
                                  )}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap font-mono">
                                  <div className="flex items-center">
                                    <span>{order.order_id}</span>
                                    <button
                                      className="ml-2 text-gray-400 hover:text-green-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyItem(order.order_id);
                                      }}
                                    >
                                      <IoCopyOutline />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap font-mono">
                                  <div className="flex items-center">
                                    <span>{order.customer_order_id}</span>
                                    <button
                                      className="ml-2 text-gray-400 hover:text-green-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyItem(order.customer_order_id);
                                      }}
                                    >
                                      <IoCopyOutline />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  {order.source || "—"}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <span>{dueDateValue || "—"}</span>
                                    {veryLate && (
                                      <span
                                        title="Past its due date — very late"
                                        className="px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-red-600 text-white"
                                      >
                                        Very Late
                                      </span>
                                    )}
                                    {lateToday && (
                                      <span
                                        title="Due today, past the cutoff time"
                                        className="px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-red-400 text-gray-900"
                                      >
                                        Late
                                      </span>
                                    )}
                                    {hasNote && (
                                      <span
                                        title="This order has a note"
                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-yellow-500 text-gray-900"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-800" />
                                        Note
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td
                                  className="px-3 py-3 text-xs max-w-xs truncate"
                                  title={order.notes || ""}
                                >
                                  {order.notes || "—"}
                                </td>
                                {activeTab === "mugboxes" && (
                                  <>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                      {order.mug_qty ?? "—"}
                                    </td>
                                    <td className="px-3 py-3">
                                      {(order.boxes || []).length === 0 ? (
                                        "—"
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          {order.boxes.map((b) => (
                                            <div
                                              key={b.size}
                                              className="flex items-center gap-1.5"
                                            >
                                              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded text-white text-xs font-bold tabular-nums bg-teal-600">
                                                {b.count}×
                                              </span>
                                              <span className="text-gray-300 whitespace-nowrap">
                                                {b.size}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </td>
                                  </>
                                )}
                                <td className="px-3 py-3 whitespace-nowrap">
                                  {order.status}
                                </td>
                                <td className="px-3 py-3">
                                  {order.item_count}
                                </td>
                                <td
                                  className="px-3 py-3 text-xs max-w-xs truncate"
                                  title={order.sku_list}
                                >
                                  {order.sku_list || "—"}
                                </td>
                                <td
                                  className="px-3 py-3 whitespace-nowrap"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <OrderArtworkButton
                                    orderId={order.order_id}
                                    preloadedArtworks={toPreloadedArtworks(
                                      order.items,
                                    )}
                                    system="SP"
                                    size="sm"
                                  />
                                </td>
                              </tr>
                              {isExpanded && (
                                <>
                                  <tr className="bg-gray-900">
                                    <td
                                      colSpan="10"
                                      className="px-6 py-2 text-xs uppercase text-gray-500 border-b border-gray-700"
                                    >
                                      Order {order.order_id} —{" "}
                                      {order.item_count} item
                                      {order.item_count === 1 ? "" : "s"}
                                    </td>
                                  </tr>
                                  <tr className="bg-gray-900 text-xs uppercase text-gray-500">
                                    <td colSpan="2" className="px-6 py-1">
                                      Order Item ID
                                    </td>
                                    <td colSpan="2" className="px-4 py-1">
                                      SKU
                                    </td>
                                    <td colSpan="3" className="px-4 py-1">
                                      Description
                                    </td>
                                    <td
                                      colSpan="1"
                                      className="px-4 py-1 text-right"
                                    >
                                      Quantity
                                    </td>
                                    <td colSpan="2" className="px-4 py-1">
                                      Artwork
                                    </td>
                                  </tr>
                                  {this.renderItems(order)}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {this.renderPagination()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default SpOpenOrdersPage;
